import {Delta} from 'quill';

import {isNil} from 'ramda';

import {RichTextTemplateTokens} from '../types';
import {splitTemplateTokenText} from './splitTemplateTokenText';
import {TEMPLATE_TOKEN} from './TEMPLATE_TOKEN';
import {TemplateTokenBlot} from './TemplateTokenBlot';

type ClipboardMatcher = [string | number, (node: Node, delta: Delta) => Delta];

/**
 * Clipboard matchers for one editor: they turn known placeholders in text nodes into tokens and
 * re-check token elements by their code, so pasted token markup never trusts its label.
 */
export function createTemplateTokenClipboardMatchers(
  getLabel: RichTextTemplateTokens['getLabel']
): ClipboardMatcher[] {
  const matchText = (_node: Node, delta: Delta) =>
    delta.ops.reduce((next, op) => {
      if (typeof op.insert !== 'string') {
        return next.push(op);
      }

      splitTemplateTokenText(op.insert, getLabel).forEach((part) =>
        part.type === 'text'
          ? next.insert(part.text, op.attributes)
          : next.insert(
              {[TEMPLATE_TOKEN.blotName]: {code: part.code, label: part.label}},
              op.attributes
            )
      );

      return next;
    }, new Delta());

  const matchTokenElement = (node: Node) => {
    const value = TemplateTokenBlot.value(node);

    if (isNil(value)) {
      return new Delta().insert((node.textContent ?? '').replace(TEMPLATE_TOKEN.guardPattern, ''));
    }

    const label = getLabel(value.code);

    return isNil(label) || label === ''
      ? new Delta().insert(TEMPLATE_TOKEN.toPlaceholder(value.code))
      : new Delta().insert({[TEMPLATE_TOKEN.blotName]: {code: value.code, label}});
  };

  return [
    [Node.TEXT_NODE, matchText],
    [`.${TEMPLATE_TOKEN.className}`, matchTokenElement],
  ];
}
